import React, { useMemo, useEffect, useRef } from "react"
import { graphql } from "@apollo/client/react/hoc"
import gql from "graphql-tag"
import { Row, Col } from "reactstrap"
import Skeleton from "react-loading-skeleton"

import "./OtherDocumentsSection.css"
import "./DocumentsTab.css"

import { cleanDocumentUrl } from "../../Material/DocumentModal/DocumentsListNew"
import { isImage, isPdf, isVideo } from "../../Material/DocumentModal/utils"
import DatetimeRenderer from "../../Material/DatetimeRenderer"
import toTitleCase from "../../Functions/toTitleCase"
import DocumentDetails from "./DocumentDetails"

const OTHER_DOCUMENT_CATEGORIES = ["inspection", "driver", "car"]

const OtherDocumentsQuery = gql`
    query OtherDocuments($categoryId: ID!, $isInspection: Boolean!, $isDriver: Boolean!, $isCar: Boolean!) {
        inspectionDocuments: allCarPictures(incidentId: $categoryId) @include(if: $isInspection) {
            edges {
                node {
                    id
                    dateAdded
                    pictureUrl
                    pictureType
                    formType
                }
            }
        }
        driverDocuments: allDriverDocuments(driverId: $categoryId, orderBy: ["-date_added"], isArchived: false) @include(if: $isDriver) {
            edges {
                node {
                    id
                    dateAdded
                    documentUrl
                    documentSlug
                    name
                    isArchived
                    documentType {
                        id
                        typeName
                    }
                }
            }
        }
        carDocuments: allCarDocuments(car_Id: $categoryId, orderBy: ["-date_added"], isArchived: false) @include(if: $isCar) {
            edges {
                node {
                    id
                    dateAdded
                    documentUrl
                    documentSlug
                    name
                    isArchived
                    documentType {
                        id
                        typeName
                    }
                }
            }
        }
    }
`

const normalizeDocumentCategory = (category) => (category || "inspection").toLowerCase()

const getRawDocumentUrl = (document = {}) =>
    document.pictureUrl || document.documentUrl || document.url || ""

const cleanGDriveUrl = (url = "", name = "") => {
    if (!url.includes("drive.google.com")) return url
    if (isImage(url, name)) {
        if (!(url.includes("/d/") || url.includes("uc?id="))) return url
        let id = url.includes("/d/") ? url.split("/d/")[1] : url.split("uc?id=")[1]
        id = id.includes("&export=download") ? id.replace("&export=download", "") : id.split("/view")[0]

        return `https://drive.google.com/thumbnail?id=${id}&sz=w1000`.replace("%2F", "/").replace("&export=download", "")
    }

    if (url.includes("&export=download")) {
        return `https://drive.google.com/file/d/${url.split("uc?id=")[1].replace("&export=download", "/preview?usp=drivesdk")}`.replace("%2F", "/")
    }

    return url.replace("view", "preview").replace("%2F", "/").replace("&export=download", "")
}

const getDocumentUrl = (document = {}) => {
    const rawUrl = getRawDocumentUrl(document)
    const cleanedUrl = cleanDocumentUrl(rawUrl)
    return cleanGDriveUrl(cleanedUrl, document.name)
}

const getDocumentLabel = (document = {}, category = "inspection") => {
    if (category === "inspection") return document.pictureType || "Inspection Photo"
    return document.documentType?.typeName || document.name || "Document"
}

const getEmptyIconClass = (category) => {
    const iconMap = {
        driver: "fa fa-id-card-o",
        car: "fa fa-car",
        inspection: "fa fa-camera",
    }

    return iconMap[category] || "fa fa-file-text-o"
}

const getPreviewOptions = (category, categoryId, refetchOtherDocuments) => ({
    objectType: category === "inspection" ? "picture" : category,
    isPicture: true,
    driverId: category === "driver" ? categoryId : null,
    refetch: refetchOtherDocuments,
})

const buildDisplayDocument = (document, category) => {
    const documentLabel = getDocumentLabel(document, category)
    const documentUrl = getDocumentUrl(document)

    return {
        id: document.id,
        url: documentUrl,
        documentUrl,
        pictureUrl: documentUrl,
        document: document.documentSlug || document.name || documentLabel,
        documentType: document.documentType || { typeName: documentLabel },
        description: document.description || document.notes,
        dateAdded: document.dateAdded,
        isArchived: document.isArchived,
        objectType: category,
        label: documentLabel,
    }
}

const buildSelectableDoc = (document, category) => {
    const displayDocument = buildDisplayDocument(document, category)

    if (!isImage(displayDocument.documentUrl, displayDocument.label)) return null

    return {
        id: displayDocument.id,
        pictureUrl: displayDocument.documentUrl,
        url: displayDocument.documentUrl,
        documentUrl: displayDocument.documentUrl,
        document: displayDocument.document,
        documentType: displayDocument.documentType,
        objectType: category,
    }
}

const isDocumentSelected = (selectedDocs = [], document = {}) =>
    selectedDocs.some((doc) => (
        doc.id === document.id ||
        doc.documentUrl === document.documentUrl ||
        doc.url === document.url ||
        doc.pictureUrl === document.pictureUrl
    ))

const getSelectableSignature = (selectableDocs = []) => selectableDocs.map((doc) => `${doc.id}:${doc.documentUrl}`).join("|")

const renderPreview = (documentUrl, documentLabel, idx) => {
    if (isImage(documentUrl, documentLabel)) {
        return <img className="other-documents-thumb-media" src={documentUrl} alt={documentLabel || "Document"} />
    }

    if (isVideo(documentUrl, documentLabel)) {
        return <video className="other-documents-thumb-media" src={documentUrl} playsInline muted preload="metadata" />
    }

    if (isPdf(documentUrl, documentLabel)) {
        return <iframe scrolling="no" className="other-documents-thumb-media" src={documentUrl} title={`other-document-iframe-${idx}`} />
    }

    return (
        <div className="other-documents-thumb-fallback">
            <i className="fa fa-file-text-o" aria-hidden="true" />
        </div>
    )
}

const EmptyState = ({ category }) => (
    <div className="documents-empty-state-wrap">
        <div className="documents-empty-state">
            <span className="documents-empty-state__icon">
                <i className={getEmptyIconClass(category)} aria-hidden="true" />
            </span>
            <h3 className="documents-empty-state__title mb-0">No Documents Yet</h3>
            <p className="documents-empty-state__description">
                Upload documents or request missing ones to keep this section complete.
            </p>
        </div>
    </div>
)

const LoadingState = () => (
    <Row>
        {Array.from({ length: 10 }).map((_, index) => (
            <Col className="mt-3" xs={4} key={index}>
                <Skeleton count={1} height={50} />
            </Col>
        ))}
    </Row>
)

const OtherDocumentsSection = ({ documents = [], documentsLoading = false, loading = false, selectedDocs = [], documentCategory = "inspection", categoryId = null, refetchOtherDocuments, onToggleSelect, onSelectableDocsChange, onPreview, }) => {
    const category = normalizeDocumentCategory(documentCategory)
    const lastSelectableSignatureRef = useRef("")

    const displayDocuments = useMemo(() => documents.map(({ node }) => ({
        raw: node,
        display: buildDisplayDocument(node, category),
        selectable: buildSelectableDoc(node, category),
    })),
        [documents, category]
    )

    const selectableDocs = useMemo(() => displayDocuments.map(({ selectable }) => selectable).filter(Boolean),
        [displayDocuments]
    )

    useEffect(() => {
        if (!onSelectableDocsChange) return

        const nextSignature = getSelectableSignature(selectableDocs)

        if (lastSelectableSignatureRef.current === nextSignature) return

        lastSelectableSignatureRef.current = nextSignature
        onSelectableDocsChange(selectableDocs)
    }, [selectableDocs, onSelectableDocsChange])

    const previewOptions = useMemo(() => getPreviewOptions(category, categoryId, refetchOtherDocuments),
        [category, categoryId, refetchOtherDocuments]
    )

    const inspectionDate = documents[0]?.node?.dateAdded || null
    const sourceLabel = documents[0]?.node?.formType || "VGM Admin App"
    const hasDocuments = documents.length > 0
    const isInspection = category === "inspection"

    if (documentsLoading) return <LoadingState />

    if (!hasDocuments) return <EmptyState category={category} />

    if (!isInspection) {
        return (
            <div className="DocumentCardContainer">
                {displayDocuments.map(({ display, selectable }, idx) => (
                    <DocumentDetails
                        key={display.id}
                        document={display}
                        isSelected={isDocumentSelected(selectedDocs, display)}
                        toggleSelectDoc={() => selectable && onToggleSelect?.(selectable)}
                        viewDocuments={() => onPreview?.(documents, idx, previewOptions)}
                        showEditChip={false}
                    />
                ))}
            </div>
        )
    }

    return (
        <div className="other-documents">
            <div className="other-documents-header">
                <div className="other-documents-heading">
                    <div className="other-documents-title">{sourceLabel}</div>
                    <div className="other-documents-date">
                        {inspectionDate && <DatetimeRenderer datetime={inspectionDate} />}
                    </div>
                </div>
            </div>
            <div className="other-documents-gallery">
                {displayDocuments.map(({ display, selectable }, idx) => {
                    const canSelect = !!selectable
                    return (
                        <div key={display.id} role="button" tabIndex={0} className={`other-documents-thumb${loading ? " is-disabled" : ""}`} onClick={() => !loading && onPreview?.(documents, idx, previewOptions)}>
                            {canSelect && (
                                <input
                                    type="checkbox"
                                    className="other-documents-checkbox"
                                    checked={isDocumentSelected(selectedDocs, display)}
                                    disabled={loading}
                                    onClick={(e) => e.stopPropagation()}
                                    onChange={() => onToggleSelect?.(selectable)}
                                />
                            )}

                            {renderPreview(display.documentUrl, display.label, idx)}
                            <span className="other-documents-thumb-label">
                                {toTitleCase(display.label || "Other")}
                            </span>
                        </div>
                    )
                })}
            </div>
        </div>
    )
}

export default graphql(OtherDocumentsQuery, {
    skip: ({ documentCategory, categoryId }) => {
        const category = normalizeDocumentCategory(documentCategory)
        return !categoryId || !OTHER_DOCUMENT_CATEGORIES.includes(category)
    },
    options: ({ documentCategory, categoryId }) => {
        const category = normalizeDocumentCategory(documentCategory)

        return {
            variables: {
                categoryId,
                isInspection: category === "inspection",
                isDriver: category === "driver",
                isCar: category === "car",
            },
            fetchPolicy: "network-only",
        }
    },
    props: ({ data, ownProps }) => {
        const category = normalizeDocumentCategory(ownProps.documentCategory)

        const documentsByCategory = {
            inspection: data?.inspectionDocuments?.edges || [],
            driver: data?.driverDocuments?.edges || [],
            car: data?.carDocuments?.edges || [],
        }

        return {
            documents: documentsByCategory[category] || [],
            documentsLoading: data?.loading || false,
            refetchOtherDocuments: data?.refetch || null,
        }
    },
})(OtherDocumentsSection)